// src/pages/mon-hoc/MonHocPage.tsx

import React, { useEffect, useState } from 'react';
import Page from '../../../components-ui/page';
import Datatable from '../../../components-ui/data-table/mui/DatatableGroup';

import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlusCircle, faTrash, faSearch, faArrowRotateForward } from '@fortawesome/free-solid-svg-icons';

import CreateOrEditMonHocModal from './CreateOrEditMonHocModal';
import { MonHocService } from '../../../services/mon-hoc/monHocService';
import { useAppSelector } from '../../../hooks/useAppSelector';

import {
    clearSelectedMonHoc,
    deleteMonHocSuccess,
    selectMonHoc,
    setMonHocCreateOrEditModel,
    setMonHocList,
    setMonHocShowConfirmDeleteModal,
    setMonHocShowCreateOrEditModal
} from '../../../faetures/mon-hoc/monHocSlice';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import Button from '../../../components-ui/button/Button';
import SearchInput from '../../../components-ui/input/search-input';
import ConfirmDeleteCustom from '../components/ConfirmDeleteCustom';

const MonHocPage = () => {
    const dispatch = useDispatch();
    const {
        listData,
        isShowCreateOrEditModal,
        createOrEditModel,
        selectedId,
        status,
        isShowConfirmDeleteModal
    } = useAppSelector((state) => state.monhoc);

    // ====== STATE SEARCH / FILTER ======
    const [filteredList, setFilteredList] = useState<any[]>(listData ?? []);
    const [resetSearchCounter, setResetSearchCounter] = useState(0);

    // ====== LOAD DỮ LIỆU ======
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await MonHocService.getAll();
                dispatch(setMonHocList(response));
            } catch (error) {
                console.error('Failed to fetch mon hoc data:', error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    // Đồng bộ filteredList khi listData thay đổi
    useEffect(() => {
        setFilteredList(listData ?? []);
    }, [listData]);

    // ====== SEARCH MÔN HỌC ======
    const onSearchMonHoc = (value: string) => {
        const keyword = value.trim().toLowerCase();
        if (!keyword) {
            setFilteredList(listData ?? []);
            return;
        }

        const result = (listData ?? []).filter((item: any) => {
            const fields = [
                item.Ky_hieu,
                item.Ten_mon,
                item.So_tin_chi?.toString(),
                item.Ly_thuyet?.toString(),
                item.Thuc_hanh?.toString()
            ];
            return fields.some((f) => (f || '').toLowerCase().includes(keyword));
        });

        setFilteredList(result);
    };

    const tong = filteredList.length;

    // ====== RENDER ======
    return (
        <Page title="Danh sách môn học">
            <div className="content">
                <div className="container-fluid">
                    {/* HEADER */}
                    <div className="ts-page-header mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="ts-page-icon rounded-circle d-flex align-items-center justify-content-center">
                                📘
                            </div>
                            <div>
                                <h4 className="mb-1">DANH SÁCH MÔN HỌC</h4>
                                <div className="text-muted small">
                                    Quản lý danh sách môn học, số tín chỉ, lý thuyết và thực hành.
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="small-box bg-info mb-0">
                                <div className="inner">
                                    <h4 className="mb-0">{tong}</h4>
                                    <p className="mb-0">Tổng số môn học</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TOOLBAR: SEARCH + THÊM MỚI */}
                    <div className="card mb-3 shadow-sm">
                        <div className="card-body py-2">
                            <div
                                className="d-flex flex-wrap align-items-center justify-content-between"
                                style={{ gap: 12 }}
                            >
                                {/* Search */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                    <SearchInput
                                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                                        size="small"
                                        sx={{ width: '260px' }}
                                        onSearch={onSearchMonHoc}
                                        autoSearch={true}
                                        resetSignal={resetSearchCounter}
                                        placeholder="Tìm mã môn, tên môn..."
                                    />

                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<FontAwesomeIcon icon={faArrowRotateForward} />}
                                        onClick={() => {
                                            setFilteredList(listData ?? []);
                                            setResetSearchCounter((prev) => prev + 1);
                                        }}
                                    >
                                    </Button>
                                </div>

                                {/* Thêm mới */}
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                    <Button
                                        startIcon={<FontAwesomeIcon icon={faPlusCircle} />}
                                        onClick={() => {
                                            dispatch(
                                                setMonHocCreateOrEditModel({
                                                    ID_mon_hoc: 0,
                                                    Ky_hieu: '',
                                                    Ten_mon: '',
                                                    So_tin_chi: 0,
                                                    Ly_thuyet: 0,
                                                    Thuc_hanh: 0
                                                })
                                            );
                                            dispatch(setMonHocShowCreateOrEditModal(true));
                                        }}
                                    >
                                        Thêm mới
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BẢNG DANH SÁCH MÔN HỌC */}
                    <Box>
                        <Datatable
                            showPagination={true}
                            showCheckBox={false}
                            columns={[
                                {
                                    caption: 'Mã môn học',
                                    dataField: 'Ky_hieu',
                                    align: 'center',
                                    width: 120
                                },
                                {
                                    caption: 'Tên môn học',
                                    dataField: 'Ten_mon',
                                    align: 'left'
                                },
                                {
                                    caption: 'Số tín chỉ',
                                    dataField: 'So_tin_chi',
                                    align: 'center',
                                    width: 100
                                },
                                {
                                    caption: 'Lý thuyết',
                                    dataField: 'Ly_thuyet',
                                    align: 'center',
                                    width: 90
                                },
                                {
                                    caption: 'Thực hành',
                                    dataField: 'Thuc_hanh',
                                    align: 'center',
                                    width: 90
                                },
                                {
                                    caption: 'Thao tác',
                                    align: 'center',
                                    width: 170,
                                    renderCell(data: any) {
                                        return (
                                            <Box
                                                display={'flex'}
                                                flexDirection="row"
                                                justifyContent="center"
                                                alignItems={'center'}
                                                gap={1}
                                            >
                                                <Tooltip title="Xem chi tiết môn học">
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => {
                                                            dispatch(selectMonHoc(data.ID_mon));
                                                            dispatch(setMonHocCreateOrEditModel(data));
                                                            dispatch(setMonHocShowCreateOrEditModal(true));
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faEye} size="xs" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Sửa môn học">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => {
                                                            dispatch(selectMonHoc(data.ID_mon));
                                                            dispatch(setMonHocCreateOrEditModel(data));
                                                            dispatch(setMonHocShowCreateOrEditModal(true));
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} size="xs" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Xóa môn học">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            dispatch(setMonHocShowConfirmDeleteModal(data.ID_mon));
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} size="xs" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        );
                                    }
                                }
                            ]}
                            data={filteredList}
                        />
                    </Box>
                </div>
            </div>

            {/* MODAL THÊM / SỬA */}
            {isShowCreateOrEditModal && (
                <CreateOrEditMonHocModal
                    isShow={true}
                    onClose={() => {
                        dispatch(setMonHocShowCreateOrEditModal(false));
                        dispatch(clearSelectedMonHoc());
                    }}
                    model={createOrEditModel}
                    onSave={() => {
                        // Sau khi lưu trong modal thành công sẽ tự dispatch status,
                        // ở đây chỉ cần đóng modal.
                        dispatch(setMonHocShowCreateOrEditModal(false));
                    }}
                />
            )}

            {/* MODAL XÁC NHẬN XÓA */}
            {isShowConfirmDeleteModal && (
                <ConfirmDeleteCustom
                    open={isShowConfirmDeleteModal}
                    onCancel={() => {
                        dispatch(setMonHocShowConfirmDeleteModal(0));
                    }}
                    onConfirm={() => {
                        MonHocService.delete(selectedId ?? 0).then((response) => {
                            if (response) {
                                dispatch(deleteMonHocSuccess(selectedId));
                                dispatch(clearSelectedMonHoc());
                                dispatch(setMonHocShowConfirmDeleteModal(0));
                            }
                        });
                    }}
                />
            )}
        </Page>
    );
};

export default MonHocPage;